/*
 * Copyright © 2021-present Protonbase Inc. All rights reserved.
 * The information contained in this software is confidential and proprietary to Protonbase Inc. and its subsidiaries. No part of this software may be used, copied, reproduced, modified, displayed, performed, executed, published, distributed, conveyed, stored, transmitted, or otherwise exploited in any form or by any means, including but not limited to electronic, mechanical, photocopying, recording, transcribing, without the prior written consent of Protonbase Inc.
 * Any violation of the above terms will constitute an infringement of copyright and will be subjected to legal action.
 */
import { Space } from 'antd'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { PlatformAnchor } from '@/components/PlatformAnchor'
import { PlatformList } from '@/components/PlatformList'
import { TPlatformListService } from '@/components/PlatformList/interface'
import { useEnumFilter } from '@/hooks/useEnumFilter'
import { useTimeRangeFilter } from '@/hooks/useTimeRangeFilter'
import { LayoutOthers } from '@/layouts/LayoutOthers'
import { useFrozenDetailsColumns } from '@/pages/billing/IncomeExpense/FrozenDetails/useFrozenDetailsColumns'
import { FrozenType } from '@/pages/billing/interface'
import { accountClient } from '@/services/cloudClient/accountClient'
import { createOrders } from '@/utils/createOrders'

export const FROZEN_DETAILS = 'frozen_details'

export function FrozenDetails() {
  const { t } = useTranslation()
  const { contractId } = useParams()
  const { filter: typeFilter, value: type } = useEnumFilter<FrozenType>(FrozenType, t('All Types'))
  const { filter: timeRangeFilter, timeRange } = useTimeRangeFilter(null, { allowClear: true })
  const columns = useFrozenDetailsColumns()
  const emitter = PlatformList.useCreateEmitter()

  const service: TPlatformListService = ({ pagination, sorter }, options) => {
    const { current, pageSize } = pagination || {}
    return accountClient.frozenApi.listFrozenDetails(
      contractId,
      {
        pageNum: current,
        pageSize,
        types: type ? [type] : undefined,
        start: timeRange?.[0]?.valueOf(),
        end: timeRange?.[1]?.valueOf(),
        ...createOrders(sorter),
      },
      options,
    )
  }

  useEffect(() => {
    emitter.fire({
      pagination: {
        current: 1,
        pageSize: 10,
      },
    })
  }, [type, timeRange])

  return (
    <LayoutOthers.ContentCard title={<PlatformAnchor id={FROZEN_DETAILS}>{t('Frozen Details')}</PlatformAnchor>} isTable>
      <PlatformList
        emitter={emitter}
        service={service}
        columns={columns}
        rowKey={record => record.gmtTime + record.type}
        createTitle={() => (
          <>
            <Space>
              {typeFilter}
              {timeRangeFilter}
            </Space>
          </>
        )}
      />
    </LayoutOthers.ContentCard>
  )
}
